'use client';

import { GuidedTourProvider } from './GuidedTourProvider';
import { GuidedTourOverlay } from './GuidedTourOverlay';
import { GuidedTourIndex } from './GuidedTourIndex';
import { GuidedTourWelcomeOverlay } from './GuidedTourWelcomeOverlay';
import { GuidedTourObjectiveIndex } from './GuidedTourObjectiveIndex';
import { GuidedTourFormatPicker } from './GuidedTourFormatPicker';
import { GuidedTourRunner } from './GuidedTourRunner';
import { TourChatPanel } from './TourChatPanel';
import { GuidedTourVideoCard } from './GuidedTourVideoCard';
import { GuidedTourVideoLibrary } from './GuidedTourVideoLibrary';
import { GuidedTourMediaManager } from './GuidedTourMediaManager';
import { GuidedTourQuickGuide } from './GuidedTourQuickGuide';
import { GuidedTourMinimized } from './GuidedTourMinimized';
import { GuidedTourCelebration } from './GuidedTourCelebration';
import { GuidedTourSandbox } from './GuidedTourSandbox';

interface GuidedTourProps {
  moduleKey?: string;
  activeTab?: string;
  role?: string;
  userId?: string;
  onNavigate?: (tab: string) => void;
  /** Index presentation mode: 'compact' (default) or 'overlay' (full-screen welcome). */
  indexMode?: 'compact' | 'overlay';
  /** When true, the tour is embedded in a modal — no index/overlay, auto-opens in chat mode. */
  embeddedInModal?: boolean;
  /** The flow to auto-select when embeddedInModal is true. */
  autoFlowId?: string;
  /** Tab ids this mount's onNavigate can switch to. Scoped claims are
   *  limited to flows whose target_tab is navigable — flows hosted on
   *  other page tabs fall through to the page-level provider. */
  navigableTabs?: string[];
  /**
   * Scope the flow index (and coach-open priority) to a specific set of
   * flows — e.g. the flows that belong to a modal or designer context.
   */
  contextFlowIds?: string[];
}

export function GuidedTour({
  moduleKey = 'total-operations',
  activeTab = 'dashboard',
  role = 'all',
  userId,
  onNavigate,
  indexMode = 'compact',
  embeddedInModal = false,
  autoFlowId,
  navigableTabs,
  contextFlowIds,
}: GuidedTourProps) {
  return (
    <GuidedTourProvider
      defaultModuleKey={moduleKey}
      defaultActiveTab={activeTab}
      role={role}
      userId={userId}
      onNavigate={onNavigate}
      indexMode={indexMode}
      embeddedInModal={embeddedInModal}
      autoFlowId={autoFlowId}
      navigableTabs={navigableTabs}
      contextFlowIds={contextFlowIds}
    >
      <GuidedTourOverlay>
        {embeddedInModal ? (
          // Modal-embedded mode: no floating launcher or welcome overlay, but
          // still mount the index and runner so the chat can hand off to a
          // step-by-step tour ("Show me") or the flow index.
          <>
            <GuidedTourIndex />
            <GuidedTourRunner />
            <TourChatPanel />
            <GuidedTourMinimized />
            <GuidedTourCelebration />
          </>
        ) : (
          <>
            {/* In overlay mode, the welcome overlay replaces the compact index */}
            {indexMode === 'overlay' ? <GuidedTourWelcomeOverlay /> : <GuidedTourIndex />}
            <GuidedTourObjectiveIndex />
            <GuidedTourFormatPicker />
            <GuidedTourRunner />
            <TourChatPanel />
            <GuidedTourVideoCard />
            <GuidedTourQuickGuide />
            <GuidedTourMinimized />
            <GuidedTourCelebration />
          </>
        )}
        {/* Mounted in both modes — the index card dispatches
            guided-tour-video-library-open even when embedded in a modal.
            The media manager (dev+root) follows the same pattern. */}
        <GuidedTourVideoLibrary />
        <GuidedTourMediaManager />
      </GuidedTourOverlay>
      {!embeddedInModal && <GuidedTourSandbox moduleKey={moduleKey} />}
    </GuidedTourProvider>
  );
}
